import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import {
  Home,
  TrendingUp,
  History,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  Phone,
  Shield,
  Building2,
  BarChart3,
  PhoneCall,
  UserCheck,
  Headphones,
  FileText,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import RealTimeNotifications from '@/components/shared/RealTimeNotifications';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [navOpen, setNavOpen] = useState(false);

  // Role-based navigation
  const navigation = useMemo(() => {
    if (user?.role === 'admin') {
      return [
        { name: 'Admin Dashboard', href: '/admin', icon: Shield },
        { name: 'Kullanıcı Yönetimi', href: '/admin/users', icon: Users },
        { name: 'Banka Yönetimi', href: '/admin/banks', icon: Building2 },
        { name: 'Personel Yönetimi', href: '/admin/agents', icon: UserCheck },
        { name: 'Takım Yönetimi', href: '/admin/teams', icon: Users },
        { name: 'Görüşmeler', href: '/admin/conversations', icon: Phone },
        { name: 'Yatırımlar', href: '/admin/investments', icon: BarChart3 },
        { name: 'VoIP Ayarları', href: '/admin/voip-settings', icon: Headphones },
        { name: 'Sistem Ayarları', href: '/admin/settings', icon: Settings },
      ];
    } else if (user?.role === 'callcenter_admin') {
      return [
        { name: 'Call Center Dashboard', href: '/callcenter', icon: PhoneCall },
        { name: 'Görüşmeler', href: '/callcenter/gorusmeler', icon: Phone },
        { name: 'Müşteri Yönetimi', href: '/callcenter/musteri-yonetimi', icon: Users },
        { name: 'Personel Yönetimi', href: '/callcenter/personel-yonetimi', icon: UserCheck },
        { name: 'Profil', href: '/profile', icon: User },
      ];
    } else if (user?.role === 'staff') {
      return [
        { name: 'Callcenter-Dashboard', href: '/callcenter', icon: PhoneCall },
        { name: 'Gespräche', href: '/callcenter/gorusmeler', icon: Phone },
        { name: 'Anrufverwaltung', href: '/callcenter/calls', icon: Phone },
        { name: 'Profil', href: '/profile', icon: User },
      ];
    } else {
      // customer navigation
      return [
        { name: 'Übersicht', href: '/dashboard', icon: Home },
        { name: 'Meine Anlagen', href: '/investments', icon: TrendingUp },
        { name: 'Transaktionsverlauf', href: '/transactions', icon: History },
        { name: 'Dokumente', href: '/documents', icon: FileText },
        { name: 'Profil', href: '/profile', icon: User },
      ];
    }
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentTitle = navigation.find((item) => item.href === location.pathname)?.name || 'Dashboard';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Identity + navigation bar */}
      <div className="bg-primary-950 relative">
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-gold-500 via-gold-500/40 to-transparent" />
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8 min-w-0">
              <Link to="/" className="flex items-center gap-2.5 shrink-0">
                <img src="/images/logo.png" alt="Sky View GmbH" className="h-7 w-auto" />
                <span className="hidden sm:block font-serif text-white text-base tracking-wide">Sky View</span>
              </Link>

              {/* Desktop nav */}
              <nav className="hidden lg:flex items-center gap-1 overflow-x-auto">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`relative flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium whitespace-nowrap transition-colors ${
                        active ? 'text-white' : 'text-primary-200 hover:text-white'
                      }`}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {item.name}
                      {active && <span className="absolute left-3 right-3 -bottom-[1px] h-[2px] bg-gold-500" />}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              {user?.role !== 'customer' && <RealTimeNotifications />}

              <div className="hidden sm:flex flex-col text-right leading-tight">
                <span className="text-sm font-medium text-white">{user?.name}</span>
                {user?.role !== 'customer' && (
                  <span className="text-[11px] text-primary-300 capitalize">{user?.role}</span>
                )}
              </div>

              <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                <User className="w-4 h-4 text-gold-400" />
              </div>

              <button
                onClick={handleLogout}
                className="text-primary-300 hover:text-white transition-colors"
                title="Çıkış Yap"
              >
                <LogOut className="h-5 w-5" />
              </button>

              <button
                type="button"
                className="lg:hidden text-primary-200 hover:text-white"
                onClick={() => setNavOpen(!navOpen)}
              >
                {navOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav dropdown */}
        {navOpen && (
          <div className="lg:hidden border-t border-white/10 bg-primary-950">
            <nav className="max-w-[1600px] mx-auto px-4 py-3 space-y-0.5">
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setNavOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium ${
                      active
                        ? 'bg-white/10 text-white border-l-2 border-gold-500'
                        : 'text-primary-200 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Page title strip */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="font-serif text-xl text-slate-900">{currentTitle}</h1>
        </div>
      </div>

      {/* Page content */}
      <main>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
      </main>
    </div>
  );
};

export default MainLayout;
